<template>
  <div>
    <WizardModal
      modal-id="wizard-modal"
      :is-active.sync="isWizardActive"
      :is-add="isAdd"
      :wizard-title="wizardTitle"
      :steps="wizardSteps"
      :step-icons="wizardStepIcons"
      :finishAtStep="finishAtStep"
      :current-step="currentStep"
      :current-step-key="String(currentStep)"
      :has-config="true"
      :show-previous-button="!isAdd && currentStep > 0"
      :show-save-button="!isAdd && currentStep < finishAtStep"
      :show-next-button="currentStep <= finishAtStep"
      :show-finish-button="
        (isAdd && currentStep === 2) || (!isAdd && currentStep === finishAtStep)
      "
      :activate-btn-save="activateBtnSave"
      :loading="isLoading"
      @close-wizard="closeWizardCancel"
      @prev-step="prevStep"
      @next-step="nextStep"
      @save-data="handleSave"
      @load-data="loadData"
      @reset-wizard="resetData"
      @on-submit="onSubmit"
    >
      <template #step-0="{ currentStep, loading }" v-if="wizardKey !== 0 || isAdd">
        <InfoStep
          ref="infoStepRef"
          :is-add.sync="isAdd"
          :prop-item-data="itemData"
          :prop-res-data="resData"
          :is-update="isUpdate"
          :vxgMasterAccountOptions="vxgMasterAccountOptions"
          :typeOptions="cameraTypeOptions"
          :vxgMasterAccountApiServerOptions="vxgMasterAccountApiServerOptions"
          @update:itemData="itemData = { ...itemData, ...$event }"
        />
      </template>
      <template #step-0="{ currentStep, loading }" v-else>
        <div class="spinner-overlay">
          <div class="spinner"></div>
        </div>
      </template>
      <template #step-1="{ currentStep, loading }">
        <LocationStep
          ref="locationStepRef"
          :is-add.sync="isAdd"
          :prop-item-data="itemData"
          :unitOptions="unitOptions"
          :centerOptions="centerOptions"
          :sectorOptions="sectorOptions"
          :resellerOptions="resellerOptions"
          @update:itemData="itemData = { ...itemData, ...$event }"
        />
      </template>
      <template #step-2="{ currentStep, loading }">
        <RecordingStep
          ref="recordingStepRef"
          :is-add="isAdd"
          :prop-item-data="itemData"
          :selected-limit="selectedLimit"
          @update:itemData="itemData = { ...itemData, ...$event }"
        />
      </template>
      <template #step-3="{ currentStep, loading }">
        <StreamStep
          ref="streamStepRef"
          :is-add="isAdd"
          :prop-item-data="itemData"
          :prop-res-data="resData"
          :prop-stream-data="streamData"
          :prop-is-camera-connected="isCameraConnected"
          @update:itemData="itemData = { ...itemData, ...$event }"
        />
      </template>
    </WizardModal>

    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>
    <!-- Toastification loading content export -->
    <toastification-loading-content ref="loadingToast" />
    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :center-filter.sync="centerFilter"
          :center-options="centerOptions"
          :sector-filter.sync="sectorFilter"
          :sector-options="sectorOptions"
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :vxg-master-account-filter.sync="vxgMasterAccountFilter"
          :vxg-master-account-options="vxgMasterAccountOptions"
          :online-filter.sync="onlineFilter"
          :rtsp-available-filter.sync="rtspAvailableFilter"
          :reseller-options="resellerOptions"
          :reseller-filter.sync="resellerFilter"
          :events-enabled-filter.sync="eventsEnabledFilter"
          :register-type-filter.sync="registerTypeFilter"
          :stream-formats-options="streamFormatsOptions"
          :stream-formats-filter.sync="streamFormatsFilter"
          :stream-resolutions-options="streamResolutionsOptions"
          :stream-resolutions-filter.sync="streamResolutionsFilter"
          :fps-options="fpsOptions"
          :fps-filter.sync="fpsFilter"
        />
      </template>
      <template v-slot:footer-buttons>
        <!--<b-button
          variant="secondary"
          size="sm"
          class="text-capitalize"
          :disabled="buttonUpdate"
          @click="updateBDCameras()"
        >
          {{ $tc("vxg_camera.update_from_vxg", 1) }}
        </b-button>-->
        <b-button
          variant="info"
          size="sm"
          class="text-capitalize"
          :disabled="buttonUpdate"
          @click="updateCameras"
        >
          {{ $t("actions.update") + " " + $tc("camera.title", 2) }}
        </b-button>
      </template>
    </filter-modal>

    <cameras-modal
      @loading-toast-event="loadingToastEvent"
      :vxg-master-account-filter="vxgMasterAccountFilter"
      :vxg-master-account-options="vxgMasterAccountOptions"
    />

    <!-- Table Container Card -->
    <b-card-group deck class="mx-2">
      <b-card class="m-0" no-body>
        <div class="m-2">
          <!-- Table Top -->
          <b-row>
            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label class="text-capitalize">{{ $t("table.show") }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
                data-cy="per-page-selector"
              />
              <label class="text-capitalize">{{ $t("table.entries") }}</label>
            </b-col>

            <!-- Search -->
            <b-col cols="12" md="6">
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  data-cy="search-input"
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('table.search') + '...'"
                />

                <b-button
                  v-if="$can('create', 'CameraOmnicloud')"
                  variant="secondary"
                  data-cy="automatest-add-camera-btn"
                  @click="openWizard"
                >
                  <span class="text-nowrap text-capitalize">
                    {{ $t("actions.add") + " " + $tc("camera.title", 1) }}
                  </span>
                </b-button>

                <b-button
                  @click="exportExcel()"
                  variant="success"
                  class="text-capitalize ml-1"
                  type="button"
                  :disabled="buttonExport"
                >
                  <feather-icon icon="DownloadCloudIcon" size="15" />
                  <span class="text-nowrap text-capitalize"> </span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>
        <b-table
          ref="refListTable"
          class="position-relative"
          :items="fetchList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          :empty-text="$t('no_record_found')"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #head()="data">
            <span> {{ $tc(data.label, 1) }}</span>
          </template>

          <template #head(unit.description)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <template #cell(access_token_all)="data">
            <div
              v-clipboard:copy="data.item.access_token_all"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
              style="cursor: pointer"
            >
              <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
            </div>
          </template>

          <template #cell(access_token_watch)="data">
            <div
              v-clipboard:copy="data.item.access_token_watch"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
              style="cursor: pointer"
            >
              <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
            </div>
          </template>

          <template #head(actions)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <!-- Column: status -->
          <template #cell(status)="data">
            <div style="position: relative; width: 120px" class="image-container">
              <div class="overlay" :id="`overlay-${data.item.camera.id}`"></div>
              <div class="loader" :id="`loader-${data.item.camera.id}`"></div>
              <img
                :name="getImageSrcCF(data.item.camera.id)"
                :src="defaultImage"
                :id="`image-${data.item.camera.id}`"
                style="width: 120px; border-radius: 16px; cursor: pointer"
                @click="openImageModal(data.item.camera.id)"
                v-b-modal.image_modal
              />
              <div class="img-overlay">
                <b-media vertical-align="center">
                  <template #aside>
                    <div
                      :style="`background: ${data.item.camera.online ? 'green' : 'red'};
                            width: 1.5rem;
                            height: 1.5rem;
                            border-radius: 1rem;
                            text-align: center;`"
                    ></div>
                  </template>
                </b-media>
              </div>
            </div>
          </template>

          <template
            #cell(rec_limit)="data"
            display="flex"
            justify-content="center"
            align-items="center"
          >
            <span>
              {{ transformRecLimit(data.item.rec_limit) }}
            </span>
          </template>
          <!-- Column: live_stream -->
          <template #cell(live_stream)="data">
            <div style="display: flex; align-items: center">
              <span
                style="cursor: pointer"
                v-b-tooltip.html.hover
                :title="formatStreamTooltip(data.item.stream_live_data)"
              >
                {{ data.item.live_stream }}
              </span>
            </div>
          </template>
          <!-- Column: recording_stream -->
          <template #cell(recording_stream)="data">
            <div style="display: flex; align-items: center">
              <span
                style="cursor: pointer"
                v-b-tooltip.html.hover
                :title="formatStreamTooltip(data.item.stream_recording_data)"
              >
                {{ data.item.recording_stream }}
              </span>
            </div>
          </template>
          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                  data-cy="actions"
                />
              </template>

              <b-dropdown-item
                @click="updateStatusCamera([data.item.camera.id, data.item])"
              >
                <feather-icon icon="RefreshCwIcon" />
                <span class="align-middle ml-50 text-capitalize"
                  >{{ $t("actions.update") }}
                </span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('update', 'CameraOmnicloud')"
                data-cy="automatest-edit-camera-btn"
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t("actions.edit") }}</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('delete', 'CameraOmnicloud')"
                data-cy="automatest-delete-camera-btn"
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize"
                  >{{ $t("actions.delete") }}
                </span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="
                  data.item.camera.rtsp_available && $can('refresh_flow', 'CameraProxy')
                "
                data-cy="automatest-refresh-rtsp-btn"
                @click="refreshRtpsFlow(data.item)"
              >
                <feather-icon icon="CastIcon" />
                <span class="align-middle ml-50 text-capitalize"
                  >{{ `${$t("actions.refresh")} RTSP` }}
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted text-capitalize">{{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <Pagination
                :totalList="totalList"
                :currentPage="currentPage"
                @updatePagination="update"
                :perPage="perPage"
                data-cy="pagination"
              >
              </Pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-card-group>
    <image-modal />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed, onMounted, nextTick, watch } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Filters from "./Filters.vue";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import axios from "axios";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ImageModal from "../../components/GridView/widgets/ImageModal.vue";
import CamerasModal from "./CamerasModal.vue";
import { getImageSrc } from "@/utils/cameraImageUtils.js";
import WizardModal from "@/components/WizardModal/Index.vue";
import InfoStep from "./wizard/InfoStep.vue";
import LocationStep from "./wizard/LocationStep.vue";
import RecordingStep from "./wizard/RecordingStep.vue";
import StreamStep from "./wizard/StreamStep.vue";
import { useDefineChatbot } from "@/composables/defineChatbot";
import {
  buildCameraSourceUrl,
  getCameraSourceUrlError,
  parseCameraSourceUrl,
} from "./cameraSourceUrl";

export default {
  components: {
    Sidenav,
    BreadcrumbMenu,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    ToastificationLoadingContent,
    FilterModal,
    ButtonFilters,
    ImageModal,
    CamerasModal,
    getImageSrc,
    WizardModal,
    InfoStep,
    LocationStep,
    RecordingStep,
    StreamStep,
  },
  props: {
    filters: {
      type: Object | Array,
    },
  },
  setup(_, context) {
    const isSidebarActiveByChatbot = store.getters["chatbot/getIsSidebarActive"];
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const infoStepRef = ref(null);
    const locationStepRef = ref(null);
    const recordingStepRef = ref(null);
    const streamStepRef = ref(null);

    const isWizardActive = ref(false);
    const isAdd = ref(false);
    const itemEdit = ref({});
    const currentStep = ref(0);
    const selectedLimit = ref(null);
    const wizardTitle = ref("Camera Setup Wizard");
    const wizardSteps = ref(["Información", "Ubicación", "Grabación", "Transmisión"]);
    const wizardStepIcons = ref(["ToolIcon", "MapIcon", "VideoIcon", "MonitorIcon"]);
    const finishAtStep = ref(3);
    const blankData = {
      // infoStep
      name: "",
      id_vxg_master_account: null,
      record_type: "",
      uplink: false,
      url: "",
      username: "",
      password: "",
      serial_number: "",
      mac_address: "",
      id_type: 3,
      rtsp_available: false,
      send_to_seproban: false,

      // locationStep
      id_unit: [],
      id_center: null,
      id_sector: null,
      id_omniview_reseller: null,

      streaming: 0,
      rec_mode: "",
      id_device_tag: null,
      live_stream: "",
      recording_stream: "",
      delayed_record_minutes: "00:00:10",
      forward_record_minutes: "00:00:10",
      vxg_master_url_source: "",

      path_onvif: "/onvif/device_service",
    };
    const resData = ref({});

    const streamData = ref({});
    const isCameraConnected = ref();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const originalItemData = ref(JSON.parse(JSON.stringify(blankData)));
    const isUpdate = ref(false);
    const isLoading = ref(false);
    const activateBtnSave = ref(false);
    const flagToUpdate = ref(0);
    const wizardKey = ref(0);
    const isDataFilling = ref(false); // Flag for data filling
    const noStreamsAvailable = ref(false); // Flag for no streams available camera not connected
    const isChatbotOpen = computed(() => store.state.chatbot.isOpen);
    const { minimizeChatbot } = useDefineChatbot();

    const updateLastWizardStep = () => {
      const streamsUnavailable =
        itemData.value.record_type === "rtsp" ||
        !resData.value.streamAvailable?.length ||
        !resData.value.streamAvailableConfigurations?.length;

      noStreamsAvailable.value = streamsUnavailable;
      finishAtStep.value = streamsUnavailable ? 2 : 3;
      if (currentStep.value > finishAtStep.value) {
        currentStep.value = finishAtStep.value;
      }
    };

    watch(
      () => [
        itemData.value.record_type,
        resData.value.streamAvailable,
        resData.value.streamAvailableConfigurations,
      ],
      updateLastWizardStep,
      { deep: true }
    );

    //watch to active the btn save data
    watch(
      itemData,
      (newVal, oldVal) => {
        if (oldVal && Object.keys(oldVal).length) {
          isUpdate.value = JSON.stringify(newVal) !== JSON.stringify(oldVal);
        }
        if (isDataFilling.value && flagToUpdate.value >= 4) {
          activateBtnSave.value = true;
        } else {
          flagToUpdate.value++;
        }
      },
      { deep: true }
    );

    onMounted(async () => {
      if (isSidebarActiveByChatbot) openWizard();
    });

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      originalItemData.value = JSON.parse(JSON.stringify(blankData));
      isUpdate.value = false;
      activateBtnSave.value = false;
      flagToUpdate.value = 0;
      isDataFilling.value = false;
    };

    const openWizard = () => {
      isAdd.value = true;
      isWizardActive.value = true;
      currentStep.value = 0;
      resetData();
      store.commit("chatbot/updateCanBeMaximized", false);
      if (isChatbotOpen.value) {
        minimizeChatbot();
      }
    };

    const closeWizard = async () => {
      isWizardActive.value = false;
      await nextTick();

      currentStep.value = 0;
      isUpdate.value = false;
      isAdd.value = false;
      itemEdit.value = {};
      itemData.value = JSON.parse(JSON.stringify(blankData));
      originalItemData.value = JSON.parse(JSON.stringify(blankData));
      resData.value = {};
      streamData.value = {};
      wizardKey.value++;
      finishAtStep.value = 3;
      flagToUpdate.value = 0;
      activateBtnSave.value = false;
      isDataFilling.value = false;
      isCameraConnected.value = null;
    };
    const closeWizardCancel = () => {
      currentStep.value = 0;
      isWizardActive.value = false;
      flagToUpdate.value = 0;
      activateBtnSave.value = false;
      noStreamsAvailable.value = false;
      finishAtStep.value = 3;
      isAdd.value = false;
      isDataFilling.value = false;
      isCameraConnected.value = null;
    };

    const openEdit = async (item) => {
      wizardKey.value = 0;
      isAdd.value = false;
      isWizardActive.value = true;
      finishAtStep.value = 3;

      try {
        isLoading.value = true;
        currentStep.value = 0;
        flagToUpdate.value = 0;
        activateBtnSave.value = false;
        isDataFilling.value = false;

        await nextTick();

        if (!item?.id) {
          throw new Error("Invalid camera data: missing ID");
        }

        itemEdit.value = {};
        resData.value = {};
        streamData.value = {};

        await nextTick();

        itemEdit.value = JSON.parse(JSON.stringify(item));

        const initialData = {
          ...blankData,
          id: item.id,
          name: item.name || "",
          id_unit: Array.isArray(item.camera?.units)
            ? item.camera.units.map((unit) => ({
                value: unit.id,
                label: unit.description,
                id_tenant: unit.router?.id_tenant,
              }))
            : [],
          id_center: item.id_center || null,
          id_sector: item.id_sector || null,
          id_omniview_reseller: item.id_omniview_reseller || null,
          id_vxg_master_account: item.id_vxg_master_account || null,
          id_type: item.camera_type?.id || null,
          rtsp_available: Boolean(item.camera?.rtsp_available),
          uplink: item.type === "uplink",
          record_type: item.type === "uplink" ? "uplink" : "",
          id_device_tag: item.camera?.id_device_tag || null,
          ip: item.ip || "",
          send_to_seproban: Boolean(item.camera?.send_to_seproban),
        };

        itemData.value = initialData;

        const response = await store.dispatch("vxg_camera/get", {
          id: [item.id],
        });

        if (!response?.data) {
          throw new Error("Failed to fetch camera details");
        }

        const sourceType =
          response.data.cameraType?.type ||
          response.data.vxg_camera?.type ||
          item.type ||
          "";
        const parsedSource = parseCameraSourceUrl(
          sourceType,
          response.data.vxg_camera?.url || response.data.cameraType?.url || "",
          blankData.path_onvif
        );

        const finalData = {
          ...initialData,
          ...response.data,
          ...response.data.vxg_camera,
          id_unit: initialData.id_unit, // Mantener formato de array para id_unit
          stream_live: response.data.vxg_camera?.stream_live || null,
          stream_recording: response.data.vxg_camera?.stream_recording || null,
          cameraType: response.data.cameraType || {},
          delayed_record_minutes:
            response.data.vxg_camera?.delayed_record_minutes || "00:00:10",
          forward_record_minutes:
            response.data.vxg_camera?.forward_record_minutes || "00:00:10",
          rec_mode: response.data.vxg_camera?.rec_mode || "off",
          rec_limit: response.data.vxg_camera?.rec_limit || 0,
          isCameraConnected: response.data.isCameraConnected,
          record_type: sourceType,
          url: parsedSource.url,
          path_onvif: parsedSource.path,
        };

        itemData.value = finalData;

        resData.value = {
          streamAvailableConfigurations:
            response.data.streamAvailableConfigurations || [],
          streamAvailable: response.data.vxg_camera?.streamConfigurations || [],
        };

        updateLastWizardStep();

        await nextTick();
        originalItemData.value = JSON.parse(JSON.stringify(finalData));

        streamData.value = {
          stream_live: response.data.vxg_camera?.stream_live || null,
          stream_recording: response.data.vxg_camera?.stream_recording || null,
        };

        isCameraConnected.value = response.data.isCameraConnected;

        await nextTick();
        wizardKey.value++;
        isDataFilling.value = true;
      } catch (error) {
        console.error("Error in openEdit:", error);
        toast({
          component: ToastificationContent,
          props: {
            title: "Error al cargar la cámara",
            text: error.message,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        closeWizard();
      } finally {
        isLoading.value = false;
      }
    };

    const prevStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--;
      }
    };

    const changeCenter = () => {
      store
        .dispatch("sector/getAll", {
          id_center: [itemData.value.id_center],
        })
        .then(() => {
          const sectors = Array.isArray(sectorOptions.value) ? sectorOptions.value : [];
          const sector = sectors.filter(
            (item) => item.value === itemData.value.id_sector
          );
          if (sector.length == 0) itemData.value.id_sector = "";
        });
    };

    const updateDeviceTags = () => {
      if (itemData.value.id_unit) {
        const idTenants = itemData.value.id_unit.map((unit) => unit.id_tenant);
        store.dispatch("tenant/getDeviceTagsByTenantIds", {
          id_tenants: idTenants,
        });
      }
    };

    const nextStep = async () => {
      let isValid = true;

      try {
        // Validar cada paso basado en el índice de `currentStep`
        if (currentStep.value === 0 && infoStepRef.value?.validateStep) {
          isValid = await infoStepRef.value.validateStep();
        } else if (currentStep.value === 1 && locationStepRef.value?.validateStep) {
          isValid = await locationStepRef.value.validateStep();
        } else if (currentStep.value === 2 && recordingStepRef.value?.validateStep) {
          isValid = await recordingStepRef.value.validateStep();
        } else if (currentStep.value === 3 && streamStepRef.value?.validateStep) {
          isValid = await streamStepRef.value.validateStep();
        }

        if (isValid) {
          currentStep.value++;
          if (currentStep.value === 3 && !noStreamsAvailable.value) {
            flagToUpdate.value -= 2;
          }
        }
      } catch (error) {
        console.error("Error during step validation:", error);
      }
    };

    const loadData = () => {
      if (!isAdd.value) {
        itemData.value = itemEdit.value;
        itemData.value.id_unit = itemEdit.value.camera.units.map((value) => ({
          value: value.id,
          label: value.description,
          id_tenant: value.router.id_tenant,
        }));
        itemData.value.id_device_tag = itemEdit.value.camera.id_device_tag;
        if (itemEdit.value.camera_type) {
          itemData.value.id_type = itemEdit.value.camera_type.id;
        }
        if (itemData.value.type == "uplink") {
          itemData.value.uplink = true;
          itemData.value.record_type = "uplink";
        } else itemData.value.uplink = false;
        updateDeviceTags();
        changeCenter();
      } else {
        itemData.value.rec_mode = "off";

        if (vxgMasterAccountOptions.length == 1)
          itemData.value.id_vxg_master_account = vxgMasterAccountOptions[0].value;

        if (centerOptions.length == 1) itemData.value.id_center = centerOptions[0].value;

        if (sectorOptions.length == 1) itemData.value.id_sector = sectorOptions[0].value;

        if (resellerOptions.length == 1)
          itemData.value.id_omniview_reseller = resellerOptions[0].value;
      }
    };

    const saveData = async (shouldClose = false) => {
      isLoading.value = true;

      try {
        if (!itemData.value.name || itemData.value.name.trim() === "") {
          throw new Error("El nombre de la cámara es requerido");
        }

        const sourceUrlError = getCameraSourceUrlError(
          itemData.value.record_type,
          itemData.value.url
        );
        if (sourceUrlError) throw new Error(sourceUrlError);

        let isStep3 = false;

        if (currentStep.value === 3) {
          isStep3 = true;

          const requiredFields = {
            video_encoder: itemData.value.video_encoder,
            resolution: itemData.value.resolution,
            frame_rate: itemData.value.frame_rate,
            gop: itemData.value.gop,
            bitrate: itemData.value.bitrate,
            vbr_quality: itemData.value.vbr_quality,
          };

          const isGopMissing = ref(false);

          const missingFields = Object.entries(requiredFields)
            .filter(([_, value]) => !value)
            .map(([key, _]) => key.replace(/_/g, " "))
            .map((field) => field.charAt(0).toUpperCase() + field.slice(1));

          if (missingFields.includes("Gop")) {
            isGopMissing.value = true;
            missingFields.splice(missingFields.indexOf("Gop"), 1);
          }

          if (missingFields.length > 0) {
            toast({
              component: ToastificationContent,
              props: {
                title: trans["messages.validation.missing_fields.title"],
                text: trans["messages.validation.missing_fields.text"].replace(
                  "{fields}",
                  missingFields.join(", ")
                ),
                icon: "AlertTriangleIcon",
                variant: "warning",
              },
            });
            return false;
          }

          const streamPayload = {
            id: itemData.value.id,
            id_camera_vxg: itemData.value.id_vxg,
            main_transmission: itemData.value.main_transmission,
            transmission: itemData.value.transmissionLabel,
            live_broadcast: itemData.value.live_broadcast,
            video_encoder: itemData.value.video_encoder,
            resolution: itemData.value.resolution,
            frame_rate: itemData.value.frame_rate,
            bitrate: parseInt(itemData.value.bitrate, 10),
            vbr: itemData.value.vbr,
            vbr_quality: itemData.value.vbr_quality,
            gop: isGopMissing.value ? 0 : itemData.value.gop,
            token: itemData.value.access_token_all,
          };

          if (isCameraConnected.value) {
            const streamResponse = await store.dispatch(
              "vxg_camera/updateStreamConfiguration",
              streamPayload
            );

            if (!streamResponse.success) {
              throw new Error(
                streamResponse.message || "Failed to update stream configuration"
              );
            }
          }
        }

        const params = {
          name: itemData.value.name.trim(),
          id_unit:
            Array.isArray(itemData.value.id_unit) && itemData.value.id_unit.length > 0
              ? itemData.value.id_unit.map((unit) => unit.value)
              : null,
          id_center: itemData.value.id_center || null,
          id_sector: itemData.value.id_sector || null,
          id_type: itemData.value.id_type || null,
          delayed_record_minutes: itemData.value.delayed_record_minutes || "00:00:10",
          forward_record_minutes: itemData.value.forward_record_minutes || "00:00:10",
          id_device_tag: itemData.value.id_device_tag || null,
          id_vxg_master_account: itemData.value.id_vxg_master_account || null,
          id_omniview_reseller: itemData.value.id_omniview_reseller || null,
          ip: itemData.value.ip,
          rtsp_available: itemData.value.rtsp_available,
          live_stream: itemData.value.live_stream || "",
          recording_stream: itemData.value.recording_stream || "",
          live_stream_id: itemData.value.live_stream_id || itemData.value.stream_live,
          recording_stream_id:
            itemData.value.recording_stream_id || itemData.value.stream_recording,
          uplink: itemData.value.record_type == "uplink",
          router: itemData.value.record_type == "router",
          username: itemData.value.username || "",
          password: itemData.value.password || "",
          serial_number: itemData.value.serial_number || "",
          mac_address: itemData.value.mac_address || "",
          rec_mode: itemData.value.rec_mode || "off",
          rec_limit: itemData.value.rec_limit || 0,
          id_vxg: itemData.value.id_vxg || null,
          events_enabled: Boolean(itemData.value.events_enabled),
          stream_live: itemData.value.stream_live || null,
          stream_recording: itemData.value.stream_recording || null,
          isStep3: isStep3,
          live_broadcast: itemData.value.live_broadcast || null,
          noStreamsAvailable: noStreamsAvailable.value,
          type: itemData.value.record_type,
          url: buildCameraSourceUrl(
            itemData.value.record_type,
            itemData.value.url,
            itemData.value.path_onvif
          ),
          http_port: itemData.value.http_port || "",
          rtps_port: itemData.value.rtps_port || "",
          path_onvif: itemData.value.path_onvif || "",
          mac_address: itemData.value.mac_address || "",
          serial_number: itemData.value.serial_number || "",
          send_to_seproban: itemData.value.send_to_seproban,
          stream_types: (itemData.value.stream_types || []).map((streamType) =>
            typeof streamType === "object" ? streamType.id : streamType
          ),
          vxg_master_url_source: isAdd.value
            ? itemData.value.vxg_master_url_source
            : itemData.value.vxg_master_url_source || "",
        };
        
        if (params.id_omniview_reseller == null) {
          toast({
            component: ToastificationContent,
            props: {
              title: trans["messages.validation.missing_fields.title"],
              text: trans["messages.validation.missing_fields.text"].replace(
                "{fields}",
                "Reseller"
              ),
              icon: "AlertTriangleIcon",
              variant: "warning",
            },
          });
          return false;
        }

        const response = await store.dispatch(
          isAdd.value ? "vxg_camera/add" : "vxg_camera/edit",
          isAdd.value ? params : { id: itemData.value.id, params }
        );

        if (response.success) {
          const updatedCameraUrlFlag = response?.data?.updatedCameraUrl;
          toast({
            component: ToastificationContent,
            props: {
              title: isAdd.value
                ? trans["messages.edit_save.successfully_created"]
                : updatedCameraUrlFlag === false
                ? trans["messages.edit_save.updated_successfully_withoutCameraUrl"]
                : trans["messages.edit_save.updated_successfully"],
              icon: "CheckIcon",
              variant: "success",
            },
          });

          if (!isAdd.value) {
            await nextTick();
            originalItemData.value = JSON.parse(JSON.stringify(itemData.value));
            activateBtnSave.value = false;
            flagToUpdate.value = 0;
          }

          await store.dispatch("vxg_camera/getAll", {
            q: searchQuery.value,
            max: perPage.value,
            "page[n]": currentPage.value,
            sortBy: sortBy.value,
            sortDesc: isSortDirDesc.value,
            sector: sectorFilter.value,
            center: centerFilter.value,
            id_tenant: tenantFilter.value,
            vxg_master_account: vxgMasterAccountFilter.value,
            online: onlineFilter.value,
            rtsp_available: rtspAvailableFilter.value,
            events_enabled: eventsEnabledFilter.value,
            type: registerTypeFilter.value,
            stream_formats: streamFormatsFilter.value,
          });

          refetchData();

          if (shouldClose) {
            closeWizard();
          }

          return true;
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || error.message,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return false;
      } finally {
        isLoading.value = false;
      }
    };

    const handleSave = async () => {
      const result = await saveData(false);

      if (result) {
        await nextTick();
        originalItemData.value = JSON.parse(JSON.stringify(itemData.value));
        activateBtnSave.value = false;
        flagToUpdate.value = 0;
      }

      return result;
    };

    const onSubmit = async () => {
      const saveSuccessful = await saveData(true);
      if (!saveSuccessful) {
        return false;
      }
    };

    const snapshotUrl = `${window.__env.VUE_APP_BASE_URL}/v1/services/snapshot/`;

    const action = ref("list");
    const { t } = useI18nUtils();
    const trans = {
      "messages.export.download": t("messages.export.download"),
      "messages.export.Generating_file": t("messages.export.Generating_file"),
      "messages.error.server_error": t("messages.error.server_error"),
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "messages.edit_save.successfully_created": t(
        "messages.edit_save.successfully_created"
      ),
      "messages.edit_save.updated_successfully": t(
        "messages.edit_save.updated_successfully"
      ),
      "messages.edit_save.updated_successfully_withoutCameraUrl": t(
        "messages.edit_save.updated_successfully_withoutCameraUrl"
      ),
      "messages.validation.missing_fields.title": t(
        "messages.validation.missing_fields.title"
      ),
      "messages.validation.missing_fields.text": t(
        "messages.validation.missing_fields.text"
      ),
    };
    const exportExcel = () => {
      action.value == "export";
      buttonExport.value = true;
      loadingToast.value.show(
        trans["messages.export.download"],
        trans["messages.export.Generating_file"],
        "secondary"
      );
      store
        .dispatch("vxg_camera/export", {
          q: searchQuery.value,
          max: perPage.value,
          "page[n]": currentPage.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          sector: sectorFilter.value,
          center: centerFilter.value,
          id_tenant: tenantFilter.value,
          online: onlineFilter.value,
          action: "export",
          events_enabled: eventsEnabledFilter.value,
          type: registerTypeFilter.value,
          vxg_master_account: vxgMasterAccountFilter.value,
          ...(streamFormatsFilter.value?.length && {
            stream_formats: streamFormatsFilter.value.map((item) => item.label).join(","),
          }),
          ...(streamResolutionsFilter.value?.length && {
            stream_resolutions: streamResolutionsFilter.value
              .map((item) => item.value)
              .join(","),
          }),
          ...(fpsFilter.value?.value && {
            fps: fpsFilter.value.value.join(","),
          }),
        })
        .then(() => {})
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: trans["messages.error.server_error"],
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          loadingToast.value.close();
          buttonExport.value = false;
        });
    };

    const buttonExport = ref(false);
    const toast = useToast();

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,

      refListTable,
      refetchData,
      sectorOptions,
      sectorFilter,
      centerOptions,
      centerFilter,
      unitOptions,
      tenantOptions,
      tenantFilter,
      cameraTypeOptions,
      vxgMasterAccountOptions,
      vxgMasterAccountFilter,
      onlineFilter,
      rtspAvailableFilter,
      updateBDCameras,
      buttonUpdate,
      loadingToast,

      clearSearchFilters,
      filtersCount,
      buildFilters,

      eventsEnabledFilter,
      registerTypeFilter,

      resellerOptions,
      resellerFilter,
      streamFormatsOptions,
      streamFormatsFilter,
      streamResolutionsOptions,
      streamResolutionsFilter,
      fpsOptions,
      fpsFilter,
      vxgMasterAccountApiServerOptions,
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("vxg_camera/delete", { id })
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const updateStatusCamera = (data) => {
      // Show loading toast while both requests are processing
      loadingToast.value.show(
        "Actualizando",
        "Actualizando estado de cámara...",
        "secondary"
      );

      // Create promises for both API calls
      const updateStatusPromise = store.dispatch("camera_proxy/updateStatus", data[0]);
      const updateStreamsPromise = store.dispatch("vxg_camera/updateStatus", {
        id: data[1].id,
      });

      // Execute both requests in parallel
      Promise.all([updateStatusPromise, updateStreamsPromise])
        .then(([statusResponse, streamsResponse]) => {
          loadingToast.value.close();

          if (statusResponse.success && streamsResponse) {
            toast({
              component: ToastificationContent,
              props: {
                title: "Cámara actualizada correctamente",
                icon: "CheckIcon",
                variant: "success",
              },
            });
          } else {
            // Show error if either request failed
            const errorMessage = !statusResponse.success
              ? statusResponse.message
              : streamsResponse.message;

            toast({
              component: ToastificationContent,
              props: {
                title: errorMessage,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
          refetchData();
        })
        .catch((error) => {
          loadingToast.value.close();
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || "Error actualizando la cámara",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const onCopy = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: "Copiado correctamente",
          icon: "ClipboardIcon",
          variant: "success",
        },
      });
    };

    const onError = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: "No se pudo copiar",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    const refreshRtpsFlow = (camera) => {
      axios
        .get(`${window.__env.VUE_APP_MS_RTSP_URL}refresh`, {
          params: { id: camera.camera.id },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "X-Host": window.__env.VUE_APP_BASE_URL,
          },
        })
        .then((result) => {
          toast({
            component: ToastificationContent,
            props: {
              title: "Actualizadó correctamente",
              icon: "CheckIcon",
              variant: "success",
            },
          });
        });
    };

    const openImageModal = async (imageId) => {
      const image = document.querySelector("#image-" + imageId);
      if (image) {
        const imageSrc = image.src;
        await store.dispatch("image_modal/imageUrl", imageSrc);
      }
    };

    const updateCameras = function () {
      this.$bvModal.show("vxg-cameras");
    };

    const loadingToastEvent = function (data) {
      if (data.data) {
        loadingToast.value.show("Actualizando", `Actualizando cámaras ...`, "secondary");
      } else {
        this.$bvModal.hide("filter_modal");
        loadingToast.value.close();
        resellerFilter.value = null;
        refetchData();
        toast({
          component: ToastificationContent,
          props: {
            title: "Actualizadó correctamente",
            icon: "CheckIcon",
            variant: "success",
          },
        });
      }
    };

    //Function to load and save the images uses src/utils/cameraImageUtils
    const getImageSrcCF = async (cameraId) => {
      getImageSrc(cameraId);
    };

    const transformRecLimit = (data) => {
      return Math.round(data / 24);
    };

    const formatStreamTooltip = (streamData) => {
      if (!streamData) return "";
      // Destructure to exclude unwanted fields
      const {
        id,
        id_stream,
        id_stream_media,
        id_camera,
        id_stream_config,
        resolution_id,
        pivot,
        created_at,
        updated_at,
        resolution,
        ...rest
      } = streamData;

      let lines = [];
      if (resolution) {
        lines.push(`Resolution: ${resolution.width}x${resolution.height}`);
      }
      // Add each property on a new line
      Object.entries(rest).forEach(([key, value]) => {
        lines.push(`${key}: ${value}`);
      });
      // Join with <br> for HTML tooltip
      return lines.join("<br>");
    };

    return {
      loadingToast,
      action,
      exportExcel,
      buttonExport,
      isAdd,
      itemEdit,
      openEdit,
      openWizard,
      confirmDelete,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      sectorOptions,
      sectorFilter,
      centerOptions,
      centerFilter,
      unitOptions,
      update,
      onCopy,
      onError,
      tenantOptions,
      tenantFilter,
      cameraTypeOptions,
      vxgMasterAccountOptions,
      vxgMasterAccountFilter,
      onlineFilter,
      rtspAvailableFilter,
      refreshRtpsFlow,
      snapshotUrl,
      token: localStorage.additionalTokenImage,
      updateStatusCamera,
      updateBDCameras,
      buttonUpdate,
      loadingToastEvent,

      eventsEnabledFilter,
      registerTypeFilter,

      filtersCount,
      buildFilters,
      clearSearchFilters,

      resellerOptions,
      resellerFilter,
      openImageModal,
      updateCameras,

      getImageSrcCF,
      defaultImage: "/img/cameras/image_not_available.jpg",
      transformRecLimit,

      isWizardActive,
      closeWizard,
      wizardTitle,
      wizardSteps,
      wizardStepIcons,
      currentStep,
      finishAtStep,
      prevStep,
      itemData,
      nextStep,
      infoStepRef,
      locationStepRef,
      recordingStepRef,
      streamStepRef,
      loadData,
      resetData,
      handleSave,
      onSubmit,
      selectedLimit,
      resData,
      streamData,
      isUpdate,
      wizardKey,
      isLoading,
      closeWizardCancel,
      isDataFilling,
      activateBtnSave,
      formatStreamTooltip,
      streamFormatsOptions,
      streamFormatsFilter,
      streamResolutionsOptions,
      streamResolutionsFilter,
      fpsOptions,
      fpsFilter,
      vxgMasterAccountApiServerOptions,
      isCameraConnected,
    };
  },
};
</script>
<style>
.img-overlay {
  z-index: 1;
  position: absolute;
  width: -webkit-fill-available;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  padding: 5px;
  top: 0;
  right: 0;
  left: 0;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(151, 151, 151, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3e8cc7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

</style>
